import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import TileCard from '../components/TileCard';

/**
 * Position-scoped workflow bucket — same visual pattern as MyWorkTile,
 * but the "My Work" concept is replaced with tasks assigned to everyone
 * holding a given company_position. Used on GM dashboards so they can
 * glance at what their team is sitting on.
 *
 * Pulls /api/dashboard/workflow-by-position, which returns counts
 * (open / in_process / overdue) + top_urgent items.
 *
 * Props:
 *   positionName  (required) — e.g. 'Sales Admin', 'Truck Maintenance',
 *                                   'Dispatcher'.
 *   title         (optional) — card title override.
 *   limit         (optional, default 3) — top N urgent tasks to show.
 */
export default function PositionWorkflowBucketTile({ positionName, title, limit = 3 }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    if (!positionName) { setState({ loading: false, error: 'No position', data: null }); return; }
    let cancelled = false;
    api.getDashboardWorkflowByPosition(positionName, limit)
      .then(data => { if (!cancelled) setState({ loading: false, error: null, data }); })
      .catch(err => { if (!cancelled) setState({ loading: false, error: err?.message || 'failed', data: null }); });
    return () => { cancelled = true; };
  }, [positionName, limit]);

  const counts = state.data?.counts || {};
  const top = state.data?.top_urgent || [];

  return (
    <TileCard
      title={title || `${positionName} Tasks`}
      loading={state.loading}
      error={state.error}
      actionHref="/workflow/items"
      actionLabel="All items →"
    >
      <div className="dash-stat-row">
        <div className="dash-stat-chip">
          <div className="dash-stat-chip-value">{counts.open ?? 0}</div>
          <div className="dash-stat-chip-label">Open</div>
        </div>
        <div className="dash-stat-chip">
          <div className="dash-stat-chip-value">{counts.in_process ?? 0}</div>
          <div className="dash-stat-chip-label">In Process</div>
        </div>
        <div className={`dash-stat-chip ${counts.overdue > 0 ? 'overdue' : ''}`}>
          <div className="dash-stat-chip-value">{counts.overdue ?? 0}</div>
          <div className="dash-stat-chip-label">Overdue</div>
        </div>
      </div>

      {top.length === 0 ? (
        <div className="dash-card-empty">No urgent {positionName.toLowerCase()} items.</div>
      ) : (
        <div className="dash-list">
          {top.map(item => {
            const overdue = item.sla_deadline && new Date(item.sla_deadline) < new Date();
            return (
              <Link
                key={item.id}
                to={`/workflow/items/${item.id}`}
                className={`dash-list-row ${overdue ? 'overdue' : ''}`}
              >
                <div>
                  <div className="dash-list-title">{item.title}</div>
                  <div className="dash-list-sub">
                    {item.workflow_name} · {item.stage_name}
                  </div>
                </div>
                <span className={`dash-list-meta ${overdue ? 'overdue' : ''}`}>
                  {formatSla(item.sla_deadline)}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </TileCard>
  );
}

function formatSla(deadline) {
  if (!deadline) return 'No SLA';
  const now = new Date();
  const d = new Date(deadline);
  const diffMs = d.getTime() - now.getTime();
  if (diffMs < 0) {
    const h = Math.ceil(Math.abs(diffMs) / (1000 * 60 * 60));
    return h < 24 ? `${h}h over` : `${Math.ceil(h / 24)}d over`;
  }
  const h = Math.floor(diffMs / (1000 * 60 * 60));
  return h < 24 ? `${h}h left` : `${Math.floor(h / 24)}d left`;
}
